import React, { useState, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import { useModalCrudState } from '../../../../_helper/hooks/useModalCrudState';

const ReportTypeDataModal = ({ apiEndpoint, isOpen, toggle, mode, selectedReportType, refreshData }) => {
    const initialReportTypeData = {
        id: '',
        title: '',
        file: null,
        isExpertReport: false,
    };

    const validationSchema = useMemo(() => ({
        title: { required: true, message: 'Başlık zorunludur.' },
        file: {
            conditionalRequired: () => mode === 'add',
            message: 'Dosya seçmek zorunludur.',
        },
    }), [mode]);

    const {
        formData: reportTypeData,
        setFormData: setReportTypeData,
        errors,
        setErrors,
        handleCancel: baseHandleCancel,
        handleInputChange,
        handleSubmit,
        isSubmitting,
    } = useModalCrudState({
        isOpen,
        mode,
        selectedItem: selectedReportType,
        initialData: initialReportTypeData,
        toggle,
        mapSelectedToData: ({ initialData, selectedItem }) => ({
            ...initialData,
            id: selectedItem.id || '',
            title: selectedItem.title || '',
            file: null,
            isExpertReport: selectedItem.isExpertReport || false,
        }),
        normalizeInput: true,
        validationSchema,
        submitConfig: {
            apiEndpoint,
            refreshData,
            componentName: 'Rapor Şablonu',
            isFormData: true,
            buildPayload: (data) => {
                const fd = new FormData();
                fd.append('title', data.title);
                fd.append('isExpertReport', data.isExpertReport === true ? 'true' : 'false');
                if (data.file) {
                    fd.append('File', data.file, data.file.name);
                }
                if (mode === 'modify') {
                    fd.append('id', data.id);
                }
                return fd;
            },
            onSuccess: () => handleCancel(),
        },
    });
    const [currentFileName, setCurrentFileName] = useState('');

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedReportType) {
                setCurrentFileName(selectedReportType.fileName || (selectedReportType.filePath ? selectedReportType.filePath.split('/').pop() : ''));
            } else {
                setCurrentFileName('');
            }
        }
    }, [mode, selectedReportType, isOpen]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setReportTypeData(prevData => ({ ...prevData, file: file }));
            if (errors.file) {
                setErrors(prevErrors => ({ ...prevErrors, file: null }));
            }
        }
    };

    const handleCancel = () => {
        setCurrentFileName('');
        baseHandleCancel();
    };

    const getModalTitle = () => (mode === 'add' ? 'Yeni Rapor Şablonu Ekle' : 'Rapor Şablonu Düzenle');
    const getButtonLabel = () => (mode === 'add' ? 'Ekle' : 'Güncelle');

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} backdrop="static">
            <ModalHeader toggle={handleCancel}>{getModalTitle()}</ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="title">Başlık <span className="text-danger">*</span></Label>
                        <Input
                            type="text"
                            name="title"
                            id="title"
                            value={reportTypeData.title}
                            onChange={handleInputChange}
                            placeholder="Şablon başlığı girin"
                            invalid={!!errors.title}
                        />
                        <FormFeedback>{errors.title}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="file">
                            Dosya {mode === 'add' && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="file"
                            name="file"
                            id="file"
                            onChange={handleFileChange}
                            invalid={!!errors.file}
                        />
                        {mode === 'modify' && currentFileName && (
                            <small className="form-text text-muted">Mevcut dosya: {currentFileName}. Değiştirmek için yeni bir dosya seçin.</small>
                        )}
                        <FormFeedback>{errors.file}</FormFeedback>
                    </FormGroup>

                    <FormGroup className="d-flex align-items-center mb-3">
                        <Label htmlFor="isExpertReport" className="me-3 mb-0" style={{ flexShrink: 0, minWidth: '120px' }}>Rapor Türü</Label>
                        <div className="d-flex flex-grow-1">
                            <div className="form-check form-check-inline radio radio-success mt-2 me-3">
                                <Input
                                    className="form-check-input"
                                    id="isExpertReportTrue"
                                    type="radio"
                                    name="isExpertReport"
                                    checked={reportTypeData.isExpertReport === true}
                                    onChange={() => handleInputChange({ target: { name: 'isExpertReport', value: true, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="isExpertReportTrue">Eksper Raporu</Label>
                            </div>
                            <div className="form-check form-check-inline radio radio-danger mt-2">
                                <Input
                                    className="form-check-input"
                                    id="isExpertReportFalse"
                                    type="radio"
                                    name="isExpertReport"
                                    checked={reportTypeData.isExpertReport === false}
                                    onChange={() => handleInputChange({ target: { name: 'isExpertReport', value: false, type: 'radio' } })}
                                />
                                <Label className="form-check-label" htmlFor="isExpertReportFalse">Sigorta Dilekçesi</Label>
                            </div>
                        </div>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="outline-success" className="rounded-pill" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? 'Kaydediliyor...' : getButtonLabel()}
                </Button>
                <Button color="outline-danger" onClick={handleCancel} className="ms-2 rounded-pill">İptal</Button>
            </ModalFooter>
        </Modal>
    );
};

ReportTypeDataModal.propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['add', 'modify']).isRequired,
    selectedReportType: PropTypes.object,
    refreshData: PropTypes.func.isRequired,
};

export default ReportTypeDataModal;
